import {faBarsSort, faFilter, faGear} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, ConfigProvider, Dropdown, Flex, Modal, Radio, Tag, Tooltip} from 'antd';
import {Dispatch, ReactElement, SetStateAction, useMemo, useRef, useState} from 'react';
import {z as r} from 'zod';

import {appRoute} from '../../../../app-route';
import {PageHeaderTitle} from '../../../../component/page-header-title/page-header-title';
import {Text} from '../../../../component/text/text';
import {UsetifulNameProductEnum} from '../../../../component/usetiful/usetiful-const';
import {spacer_l} from '../../../../css/var-export.scss';
import {BreadCrumbs} from '../../../../layout/bread-crumbs/bread-crumbs';
import {NavigationLink} from '../../../../layout/navigation-link/navigation-link';
import {PageCard} from '../../../../layout/page-card/page-card';
import {PageHeader} from '../../../../layout/page-header/page-header';
import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {useLicenses} from '../../../../provider/license/license-hook';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {useSystem} from '../../../../provider/system/system-hook';
import {useUser} from '../../../../provider/user/user-hook';
import {ReviewSortingTypeEnum} from '../../../../service/reviews/reviews-type';
import {FeaturesEnum} from '../../../../service/user/user-type';
import {getFromLocalStorage, saveToLocalStorage} from '../../../../util/local-storage';
import {useUrl} from '../../../../util/url-hook/url-hook';
import {RequestReport} from '../request-report/request-report';
import {AddReviewButton} from '../review-list-page-content/add-review-button/add-review-button';
import {InternalAgencyGenerateButton} from '../review-list-page-content/review-list/review/internal-agency-generate-button/internal-agency-generate-button';
import {ReviewsFilterDataType} from '../review-list-page-content/reviews-filter/reviews-filter-type';
import {ReviewsStatistic} from '../review-list-page-content/reviews-statistic/reviews-statistic';
import {useReviewStateHook} from '../reviews-state/reviews-state-provider';
import {ReviewsStateType, ReviewsStateUrlQueryNameEnum} from '../reviews-state/reviews-state-type';

import {IS_SEND_ENTER_VARIANT_LOCALSTORAGE_KEY} from './reviews-header-const';
import {useShowAutotagsMutation} from './reviews-header-hook';
import * as styles from './reviews-header.scss';

type PropsType = {
    setIsFilterOpened: Dispatch<SetStateAction<boolean>>;
    isSelectMode?: boolean;
    onToggleSelectMode?: () => void;
    hasReviews?: boolean;
};

// eslint-disable-next-line max-statements, complexity
export function ReviewsHeader(props: PropsType): ReactElement {
    const {setIsFilterOpened, isSelectMode, onToggleSelectMode} = props;

    const {isShowAutotags, reviewsState, reviews, cursorPagination} = useReviewStateHook();
    const {isSourceSettingsShown} = useDomainConfig();
    const {screen} = useSystem();
    const {getLocalizedString} = useLocale();
    const {refetchUser} = useUser();
    const hasReviews = useMemo(() => Boolean(reviews?.results.length), [reviews]);
    const {snackbar} = useSnackbar();
    const {mutateAsync} = useShowAutotagsMutation();
    const mobileButtonRef = useRef<HTMLButtonElement | null>(null);
    const [openReplySettingModal, setOpenReplySettingModal] = useState<boolean>(false);
    const localStorageVariantSendReply = getFromLocalStorage(
        IS_SEND_ENTER_VARIANT_LOCALSTORAGE_KEY,
        r.boolean(),
        false
    );
    const {user} = useUser();
    const {licenses} = useLicenses();
    const [isSendEnterVariant, setIsSendEnterVariant] = useState<boolean>(localStorageVariantSendReply);
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const isShowInternalAgencyButton =
        user?.isInternalAgency && user.isStaff && licenses[FeaturesEnum.bulkGenerateButton]?.isActive;

    function handleOpenMobileFilter() {
        setIsFilterOpened((previousState) => {
            const willBeOpened = !previousState;

            if (!previousState && mobileButtonRef.current) {
                const buttonTop = mobileButtonRef.current.getBoundingClientRect().top + window.scrollY;

                window.scrollTo({
                    top: buttonTop,
                    behavior: 'smooth',
                });
            }

            return willBeOpened;
        });
    }

    async function handleAutotags() {
        await mutateAsync(!isShowAutotags);
        await refetchUser();
        await snackbar.success(
            getLocalizedString(
                !isShowAutotags
                    ? 'REVIEW__DROPDOWN_MENU__SNACKBAR__AUTOTAGS_TURNED_ON'
                    : 'REVIEW__DROPDOWN_MENU__SNACKBAR__AUTOTAGS_TURNED_OFF'
            )
        );
    }

    function handleReplySettingsClick() {
        setOpenReplySettingModal(true);
        setDropdownOpen(false);
    }

    const settingMenuItem = [
        {
            key: 'autotags',
            label: (
                <Text
                    stringKey={
                        isShowAutotags
                            ? 'REVIEW__DROPDOWN_MENU__HANDLE_OFF_AUTOTAGS'
                            : 'REVIEW__DROPDOWN_MENU__HANDLE_ON_AUTOTAGS'
                    }
                />
            ),
            onClick: () => handleAutotags(),
        },
        {
            key: 'reply-send-settings',
            label: <Text stringKey="REVIEW__DROPDOWN_MENU__REPLY_SEND_SETTINGS" />,
            onClick: handleReplySettingsClick,
        },
        ...(isSourceSettingsShown
            ? [
                  {
                      key: 'responsible',
                      label: (
                          <NavigationLink
                              className={styles.ReviewsHeader_buttonLink}
                              to={appRoute.settingsSourceSettings.path}
                          >
                              <Text stringKey="REVIEW__DROPDOWN_MENU__TO_SOURCES_SETTING" />
                          </NavigationLink>
                      ),
                  },
              ]
            : []),
    ];

    const {setQuery: setReviewsStateUrlQuery, queries} = useUrl<
        Omit<ReviewsStateType, 'filterState'> & ReviewsFilterDataType
    >();

    const isSorted = queries.ordering === ReviewSortingTypeEnum.acs;

    function handleSorting() {
        setReviewsStateUrlQuery({
            [ReviewsStateUrlQueryNameEnum.sorting]: isSorted ? ReviewSortingTypeEnum.desc : ReviewSortingTypeEnum.acs,
        });
        cursorPagination.changePagePagination(1);
    }

    function handleDropdownOpenChange(open: boolean) {
        setDropdownOpen(open);
    }

    return (
        <PageCard>
            <Flex align="center" justify="space-between">
                <BreadCrumbs
                    list={[
                        {
                            path: appRoute.reviewsManagementReviews.path,
                            titleLangKey: 'CATEGORY_NAME__REVIEWS',
                        },
                    ]}
                />
            </Flex>
            <PageHeader className={styles.ReviewsHeader}>
                <PageHeaderTitle
                    productName={UsetifulNameProductEnum.REVIEWS}
                    title="CATEGORY_NAME__REVIEWS"
                    wrapperClassName={styles.ReviewsHeader_wrapper}
                />

                <div className={styles.ReviewsHeader_buttonWrapper}>
                    <Tooltip
                        getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                        placement="topRight"
                        title={getLocalizedString('REVIEW__ADD_REVIEW__TOOLTIP')}
                    >
                        <span>
                            <AddReviewButton />
                        </span>
                    </Tooltip>
                    <Tooltip
                        getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                        placement="topRight"
                        title={getLocalizedString('REVIEWS__DOWNLOAD_THE_REPORT')}
                    >
                        <span>
                            <RequestReport hasReviews={hasReviews} reviewsState={reviewsState} />
                        </span>
                    </Tooltip>
                    <ConfigProvider
                        theme={{
                            token: {
                                motion: false, // полностью отключает анимации
                            },
                        }}
                    >
                        <Dropdown
                            getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                            menu={{items: settingMenuItem}}
                            onOpenChange={handleDropdownOpenChange}
                            open={dropdownOpen}
                            placement="bottomRight"
                            trigger={['click']}
                        >
                            <Button icon={<FontAwesomeIcon icon={faGear} />} />
                        </Dropdown>
                    </ConfigProvider>
                </div>
            </PageHeader>

            <Flex align="end">
                <div className={styles.ReviewsHeader_radioButtonWrapper}>
                    <ReviewsStatistic reviewsState={reviewsState} />
                    {isShowInternalAgencyButton && <InternalAgencyGenerateButton />}
                </div>
                <Flex align="center" gap={12} style={{marginLeft: 'auto'}}>
                    {onToggleSelectMode && !screen.isMobile && (
                        <Button disabled={!hasReviews || isSelectMode} onClick={onToggleSelectMode} type="default">
                            <Locale stringKey="BUTTON__SELECT" />
                        </Button>
                    )}
                    <Button
                        className={styles.ReviewsHeader_orderingButton}
                        // eslint-disable-next-line no-undefined
                        icon={<FontAwesomeIcon flip={!isSorted ? 'vertical' : undefined} icon={faBarsSort} />}
                        onClick={handleSorting}
                        type="link"
                    >
                        <Locale stringKey={isSorted ? 'REVIEWS__NEW' : 'REVIEWS__OLD'} />
                    </Button>
                </Flex>
            </Flex>
            {screen.isMobile && (
                <Flex gap={12} vertical>
                    <Button
                        block
                        className={styles.ReviewsHeader_filterButton}
                        icon={<FontAwesomeIcon icon={faFilter} />}
                        onClick={handleOpenMobileFilter}
                        ref={mobileButtonRef}
                        type="default"
                    >
                        <Locale stringKey="BUTTON__FILTER" />
                    </Button>
                    {onToggleSelectMode && (
                        <Button
                            block
                            disabled={!hasReviews || isSelectMode}
                            onClick={onToggleSelectMode}
                            type="default"
                        >
                            <Locale stringKey="BUTTON__SELECT" />
                        </Button>
                    )}
                </Flex>
            )}
            {openReplySettingModal && (
                <Modal
                    footer={[
                        <Button key="cancel" onClick={() => setOpenReplySettingModal(false)}>
                            <Locale stringKey="POPUP__BUTTON__CANCEL" />
                        </Button>,
                        <Button
                            key="ok"
                            onClick={() => {
                                saveToLocalStorage(isSendEnterVariant, IS_SEND_ENTER_VARIANT_LOCALSTORAGE_KEY);
                                setOpenReplySettingModal(false);
                                snackbar.success({
                                    message: (
                                        <Locale stringKey="REVIEW__DROPDOWN_MENU__REPLY_SEND_SETTINGS__SUCCESS_TITLE" />
                                    ),
                                    description: (
                                        <Locale stringKey="REVIEW__DROPDOWN_MENU__REPLY_SEND_SETTINGS__SUCCESS_DESCRIPTION" />
                                    ),
                                });
                            }}
                            type="primary"
                        >
                            <Locale stringKey="TEXT__APPLY" />
                        </Button>,
                    ]}
                    onCancel={() => setOpenReplySettingModal(false)}
                    onClose={() => setOpenReplySettingModal(false)}
                    open
                    title={<Locale stringKey="REVIEW__DROPDOWN_MENU__REPLY_SEND_SETTINGS__MODAL__TITLE" />}
                >
                    <Flex gap={spacer_l} vertical>
                        <Text secondary stringKey="REVIEW__DROPDOWN_MENU__REPLY_SEND_SETTINGS__MODAL__DESCRIPTION" />
                        <Radio.Group
                            className={styles.ReviewsHeader_modalRadioGroup}
                            defaultValue={isSendEnterVariant}
                            onChange={(variant) => setIsSendEnterVariant(variant.target.value)}
                            options={[
                                {
                                    value: false,
                                    label: (
                                        <Locale
                                            stringKey="REVIEW__DROPDOWN_MENU__REPLY_SEND_SETTINGS__MODAL__VARIANT_1"
                                            valueMap={{
                                                buttonTwo: (
                                                    <Tag className={styles.ReviewsHeader_sendReplyTag}>Shift+Enter</Tag>
                                                ),
                                                buttonOne: (
                                                    <Tag className={styles.ReviewsHeader_sendReplyTag}>Enter</Tag>
                                                ),
                                            }}
                                        />
                                    ),
                                },
                                {
                                    value: true,
                                    label: (
                                        <Locale
                                            stringKey="REVIEW__DROPDOWN_MENU__REPLY_SEND_SETTINGS__MODAL__VARIANT_2"
                                            valueMap={{
                                                buttonTwo: (
                                                    <Tag className={styles.ReviewsHeader_sendReplyTag}>Shift+Enter</Tag>
                                                ),
                                                buttonOne: (
                                                    <Tag className={styles.ReviewsHeader_sendReplyTag}>Enter</Tag>
                                                ),
                                            }}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Flex>
                </Modal>
            )}
        </PageCard>
    );
}
